import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera, Clock, ImagesSquare, Trash, VideoCamera } from "@phosphor-icons/react";
import api, { formatErr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = ["general", "activity", "event", "achievement", "sports", "celebration"];
const PHOTO_EXT = /\.(jpe?g|png|webp)$/i;
const VIDEO_EXT = /\.(mp4|mov|webm)$/i;

function expiresText(post, t) {
  if (!post.expires_at) return t("mediaMoments.schoolGallery", { defaultValue: "School Gallery" });
  const ms = new Date(post.expires_at).getTime() - Date.now();
  if (ms <= 0) return t("mediaMoments.expired", { defaultValue: "Expired" });
  const hours = Math.ceil(ms / 3600000);
  if (hours <= 48) return t("mediaMoments.expiresHours", { defaultValue: "Expires in {{hours}}h", hours });
  return t("mediaMoments.expiresDate", { defaultValue: "Expires {{date}}", date: fmtDate(post.expires_at) });
}

function PrivateMedia({ asset }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let localUrl = "";
    api.get("/files/download", { params: { path: asset.path }, responseType: "blob" })
      .then((res) => {
        if (!active) return;
        localUrl = URL.createObjectURL(res.data);
        setUrl(localUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [asset.path]);

  if (failed) return <div className="aspect-video rounded-xl bg-slate-100 grid place-items-center text-xs text-slate-400">Media unavailable</div>;
  if (!url) return <div className="aspect-video rounded-xl bg-slate-100 animate-pulse" />;
  if (asset.media_type === "video") {
    return <video src={url} controls controlsList="nodownload" preload="metadata" className="w-full max-h-72 rounded-xl bg-black object-contain" />;
  }
  return <img src={url} alt="School moment" className="w-full max-h-72 rounded-xl object-cover bg-slate-100" />;
}

function PostCard({ post, admin = false, onRefresh }) {
  const { t } = useTranslation();
  const remove = async () => {
    if (!window.confirm(t("mediaMoments.removeConfirm", { defaultValue: "Remove this media post? The media files will be deleted but the audit record will remain." }))) return;
    try {
      await api.post(`/media/posts/${post.id}/remove`);
      toast.success(t("mediaMoments.removed", { defaultValue: "Media removed" }));
      onRefresh?.();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const keepGallery = async () => {
    try {
      await api.post(`/media/posts/${post.id}/gallery`);
      toast.success(t("mediaMoments.keptGallery", { defaultValue: "Post kept in School Gallery" }));
      onRefresh?.();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <article className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm" data-testid={`media-post-${post.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wide font-extrabold px-2 py-0.5 rounded-full bg-brand-coral/10 text-brand-coralDark">
              {post.audience_type === "school" ? t("mediaMoments.entireSchool", { defaultValue: "Entire School" }) : (post.class_name || t("mediaMoments.classMoment", { defaultValue: "Class Moment" }))}
            </span>
            <span className="text-[10px] uppercase tracking-wide font-bold text-slate-400">{post.category}</span>
          </div>
          <h3 className="font-heading font-extrabold text-base text-slate-900">{post.title}</h3>
          {post.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{post.description}</p>}
        </div>
        {admin && <button onClick={remove} className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50" title={t("mediaMoments.remove", { defaultValue: "Remove" })}><Trash size={17} /></button>}
      </div>

      <div className={`grid gap-2 mt-4 ${post.assets?.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
        {(post.assets || []).map((asset) => <PrivateMedia key={asset.id} asset={asset} />)}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100">
        <div className="text-[11px] text-slate-400">
          <span className="font-semibold text-slate-500">{post.publisher_name}</span> · {fmtDate(post.created_at)}
          <span className="inline-flex items-center gap-1 ml-2"><Clock size={12} /> {expiresText(post, t)}</span>
        </div>
        {admin && post.audience_type === "school" && post.retention_type !== "gallery" && (
          <Button size="sm" variant="outline" onClick={keepGallery} className="rounded-full text-xs">
            {t("mediaMoments.keepGallery", { defaultValue: "Keep in School Gallery" })}
          </Button>
        )}
      </div>
    </article>
  );
}

function validateFiles(files, t) {
  const list = Array.from(files || []);
  let photos = 0;
  let videos = 0;
  for (const f of list) {
    if (PHOTO_EXT.test(f.name)) {
      photos += 1;
      if (f.size > 8 * 1024 * 1024) throw new Error(t("mediaMoments.photoLimit", { defaultValue: "Each photo must be 8 MB or smaller." }));
    } else if (VIDEO_EXT.test(f.name)) {
      videos += 1;
      if (f.size > 50 * 1024 * 1024) throw new Error(t("mediaMoments.videoLimit", { defaultValue: "Video must be 50 MB or smaller." }));
    } else {
      throw new Error(t("mediaMoments.fileType", { defaultValue: "Only JPG, PNG, WebP, MP4, MOV and WebM are allowed." }));
    }
  }
  if (photos > 10) throw new Error(t("mediaMoments.maxPhotos", { defaultValue: "Maximum 10 photos per post." }));
  if (videos > 1) throw new Error(t("mediaMoments.maxVideos", { defaultValue: "Maximum 1 video per post." }));
  return { list, photos, videos };
}

function videoDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const duration = video.duration;
      URL.revokeObjectURL(url);
      resolve(duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video duration"));
    };
    video.src = url;
  });
}

function Composer({ mode, classes = [], academicYearId, onCreated }) {
  const { t } = useTranslation();
  const teacher = mode === "teacher";
  const [busy, setBusy] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", category: "activity", audience_type: teacher ? "class" : "school", class_id: "", retention_type: "48h",
  });

  useEffect(() => {
    if (teacher && !form.class_id && classes.length) setForm((v) => ({ ...v, class_id: classes[0].id }));
  }, [teacher, classes, form.class_id]);

  const pick = (e) => {
    try {
      const incoming = Array.from(e.target.files || []);
      const merged = [...selectedFiles];
      for (const file of incoming) {
        const duplicate = merged.some((f) =>
          f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
        );
        if (!duplicate) merged.push(file);
      }
      const checked = validateFiles(merged, t);
      setSelectedFiles(checked.list);
      // Allow choosing the same file again after it has been removed.
      e.target.value = "";
    } catch (err) {
      e.target.value = "";
      toast.error(err.message);
    }
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((files) => files.filter((_, i) => i !== index));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedFiles.length) return toast.error(t("mediaMoments.addMediaRequired", { defaultValue: "Add at least one photo or video." }));
    if ((teacher || form.audience_type === "class") && !form.class_id) return toast.error(t("mediaMoments.chooseClass", { defaultValue: "Choose a class." }));
    setBusy(true);
    try {
      const checked = validateFiles(selectedFiles, t);
      let duration = null;
      if (checked.videos) {
        const vf = checked.list.find((f) => VIDEO_EXT.test(f.name));
        duration = await videoDuration(vf);
        if (!Number.isFinite(duration) || duration > 60) throw new Error(t("mediaMoments.videoDuration", { defaultValue: "Video must be 60 seconds or shorter." }));
      }
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("audience_type", teacher ? "class" : form.audience_type);
      if (form.class_id && (teacher || form.audience_type === "class")) fd.append("class_id", form.class_id);
      if (academicYearId) fd.append("academic_year_id", academicYearId);
      fd.append("retention_type", teacher || form.audience_type === "class" ? "48h" : form.retention_type);
      if (duration != null) fd.append("video_duration_seconds", String(duration));
      checked.list.forEach((f) => fd.append("files", f));
      await api.post("/media/posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("mediaMoments.published", { defaultValue: "Moment published" }));
      setSelectedFiles([]);
      setForm((v) => ({ ...v, title: "", description: "", category: "activity" }));
      onCreated?.();
    } catch (err) {
      toast.error(err?.message && !err?.response ? err.message : formatErr(err));
    } finally { setBusy(false); }
  };

  const isClass = teacher || form.audience_type === "class";
  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="media-moments-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>{t("mediaMoments.title", { defaultValue: "Title" })}</Label><Input required maxLength={120} className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("mediaMoments.titlePlaceholder", { defaultValue: "Science activity" })} /></div>
        <div><Label>{t("mediaMoments.category", { defaultValue: "Category" })}</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`mediaMoments.categories.${c}`, { defaultValue: c.replaceAll("_", " ") })}</SelectItem>)}</SelectContent></Select></div>
        {!teacher && <div><Label>{t("mediaMoments.audience", { defaultValue: "Audience" })}</Label><Select value={form.audience_type} onValueChange={(v) => setForm({ ...form, audience_type: v, class_id: v === "school" ? "" : form.class_id, retention_type: v === "class" ? "48h" : form.retention_type })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="school">{t("mediaMoments.entireSchool", { defaultValue: "Entire School" })}</SelectItem><SelectItem value="class">{t("mediaMoments.individualClass", { defaultValue: "Individual Class" })}</SelectItem></SelectContent></Select></div>}
        {isClass && <div><Label>{t("mediaMoments.class", { defaultValue: "Class" })}</Label><Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder={t("mediaMoments.chooseClass", { defaultValue: "Choose a class" })} /></SelectTrigger><SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>}
        {!teacher && !isClass && <div><Label>{t("mediaMoments.retention", { defaultValue: "Retention" })}</Label><Select value={form.retention_type} onValueChange={(v) => setForm({ ...form, retention_type: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="48h">48 hours</SelectItem><SelectItem value="7d">7 days</SelectItem><SelectItem value="30d">30 days</SelectItem><SelectItem value="gallery">{t("mediaMoments.schoolGallery", { defaultValue: "School Gallery" })}</SelectItem></SelectContent></Select></div>}
      </div>
      <div className="mt-4"><Label>{t("mediaMoments.description", { defaultValue: "Description" })}</Label><Textarea maxLength={1200} rows={3} className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("mediaMoments.descriptionPlaceholder", { defaultValue: "Share a short update with parents…" })} /></div>
      <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
        <label className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-300 px-4 py-2 text-sm font-bold cursor-pointer hover:bg-slate-100">
          <ImagesSquare size={17} /> {t("mediaMoments.addPhotosVideo", { defaultValue: "Add photos / video" })}
          <input type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" className="hidden" onChange={pick} />
        </label>
        <p className="text-xs text-slate-500 mt-2">{t("mediaMoments.limits", { defaultValue: "Up to 10 photos (8 MB each) + 1 video (50 MB, 60 sec). Class media expires after 48 hours." })}</p>
        {selectedFiles.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{selectedFiles.map((f, index) => <span key={`${f.name}-${f.size}-${f.lastModified}`} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-600">{VIDEO_EXT.test(f.name) ? <VideoCamera size={13} /> : <Camera size={13} />}{f.name}<button type="button" onClick={() => removeSelectedFile(index)} className="ml-1 text-slate-400 hover:text-status-red" aria-label={`Remove ${f.name}`}>×</button></span>)}</div>}
      </div>
      <Button type="submit" disabled={busy} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-7">{busy ? t("mediaMoments.publishing", { defaultValue: "Publishing…" }) : t("mediaMoments.publish", { defaultValue: "Publish" })}</Button>
    </form>
  );
}

export function SchoolMoments({ classes = [], academicYearId }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/media/posts", { params: { include_expired: false } }).then((r) => setPosts(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const visible = useMemo(() => posts.filter((p) => filter === "all" || p.audience_type === filter), [posts, filter]);
  return <div data-testid="school-moments-tab">
    <h1 className="font-heading text-2xl font-extrabold mb-1">{t("mediaMoments.schoolTitle", { defaultValue: "School Blog & Moments" })}</h1>
    <p className="text-sm text-slate-500 mb-6">{t("mediaMoments.schoolSubtitle", { defaultValue: "Share class moments or school-wide events. Class media expires automatically after 48 hours; selected school posts can remain in the School Gallery." })}</p>
    <Composer mode="admin" classes={classes} academicYearId={academicYearId} onCreated={load} />
    <div className="flex gap-2 mb-4 overflow-x-auto">{[["all","All"],["class","Class Moments"],["school","School Moments"]].map(([id,label]) => <button key={id} onClick={() => setFilter(id)} className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${filter === id ? "bg-[#00194E] text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{label}</button>)}</div>
    <div className="space-y-4">{visible.map((p) => <PostCard key={p.id} post={p} admin onRefresh={load} />)}{!visible.length && <p className="text-sm text-slate-400 text-center py-10">{t("mediaMoments.noPosts", { defaultValue: "No moments yet." })}</p>}</div>
  </div>;
}

export function TeacherMoments({ classes = [] }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const load = () => api.get("/media/posts").then((r) => setPosts(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <div data-testid="teacher-moments-tab">
    <h1 className="font-heading text-2xl font-extrabold mb-1">{t("mediaMoments.classTitle", { defaultValue: "Class Moments" })}</h1>
    <p className="text-sm text-slate-500 mb-6">{t("mediaMoments.classSubtitle", { defaultValue: "Share temporary classroom photos and short videos with parents of your assigned classes. Media expires after 48 hours." })}</p>
    <Composer mode="teacher" classes={classes} onCreated={load} />
    <div className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} />)}{!posts.length && <p className="text-sm text-slate-400 text-center py-10">{t("mediaMoments.noPosts", { defaultValue: "No moments yet." })}</p>}</div>
  </div>;
}

export function ParentMoments({ child }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get("/media/parent", { params: child?.id ? { child_id: child.id } : undefined }).then((r) => setPosts(r.data)).catch((e) => toast.error(formatErr(e)));
  }, [child?.id]);
  const visible = useMemo(() => posts.filter((p) => filter === "all" || p.audience_type === filter), [posts, filter]);
  return <div data-testid="parent-moments">
    <div className="flex items-center justify-between gap-3 mb-3"><div><p className="font-heading font-extrabold text-lg">{t("mediaMoments.parentTitle", { defaultValue: "Moments" })}</p><p className="text-xs text-slate-500">{child?.name}</p></div></div>
    <div className="flex gap-2 mb-4 overflow-x-auto">{[["all","All"],["class","Class"],["school","School"]].map(([id,label]) => <button key={id} onClick={() => setFilter(id)} className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === id ? "bg-brand-coral text-white" : "bg-white text-slate-500 border border-slate-200"}`}>{label}</button>)}</div>
    <div className="space-y-3">{visible.map((p) => <PostCard key={p.id} post={p} />)}{!visible.length && <p className="text-sm text-slate-400 text-center py-10">{t("mediaMoments.noActive", { defaultValue: "No active moments right now." })}</p>}</div>
  </div>;
}
